/**
 * @module public/js/focusmode/systemCallState.js
 * @summary Persist lightweight Focus Mode state while the native phone app is in use.
 */

/**
 * Session storage key used for native-phone Focus Mode recovery.
 *
 * @type {string}
 */
export const FOCUS_MODE_SYSTEM_CALL_STATE_KEY = 'topsail:focusmode:system-call';

/**
 * Maximum age for a native-phone Focus Mode recovery snapshot.
 *
 * @type {number}
 */
export const FOCUS_MODE_SYSTEM_CALL_STATE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/**
 * Save the current Focus Mode native-phone state for recovery after returning from the phone app.
 *
 * @param {object} [options={}] - Snapshot values to persist.
 * @param {string} [options.taskId] - Focus Mode task identifier.
 * @param {string} [options.to] - Dialed phone number.
 * @param {Storage} [options.storage] - Storage implementation, defaults to `window.sessionStorage`.
 * @param {number} [options.now=Date.now()] - Current timestamp override for tests.
 * @returns {{ taskId: string, to: string, updatedAt: number, provider: string }|null} Persisted snapshot or null when storage is unavailable.
 */
export function saveFocusModeSystemCallState({
  taskId = '',
  to = '',
  storage,
  now = Date.now(),
} = {}) {
  const targetStorage =
    storage ||
    (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  const normalizedTaskId = String(taskId || '').trim();
  if (!targetStorage || !normalizedTaskId) return null;

  const payload = {
    taskId: normalizedTaskId,
    to: String(to || '').trim(),
    updatedAt: Number(now) || Date.now(),
    provider: 'system',
  };

  targetStorage.setItem(
    FOCUS_MODE_SYSTEM_CALL_STATE_KEY,
    JSON.stringify(payload)
  );
  return payload;
}

/**
 * Load the most recent Focus Mode native-phone recovery snapshot.
 *
 * @param {object} [options={}] - Optional storage and time overrides.
 * @param {Storage} [options.storage] - Storage implementation, defaults to `window.sessionStorage`.
 * @param {number} [options.now=Date.now()] - Current timestamp override for tests.
 * @param {number} [options.maxAgeMs=FOCUS_MODE_SYSTEM_CALL_STATE_MAX_AGE_MS] - Maximum accepted snapshot age.
 * @returns {{ taskId: string, to: string, updatedAt: number, provider: string }|null} Parsed snapshot when valid.
 */
export function loadFocusModeSystemCallState({
  storage,
  now = Date.now(),
  maxAgeMs = FOCUS_MODE_SYSTEM_CALL_STATE_MAX_AGE_MS,
} = {}) {
  const targetStorage =
    storage ||
    (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  if (!targetStorage) return null;

  const raw = targetStorage.getItem(FOCUS_MODE_SYSTEM_CALL_STATE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const updatedAt = Number(parsed?.updatedAt) || 0;
    const taskId = String(parsed?.taskId || '').trim();
    const ageMs = Math.max(0, Number(now) - updatedAt);
    if (!taskId || !updatedAt || ageMs > maxAgeMs) {
      targetStorage.removeItem(FOCUS_MODE_SYSTEM_CALL_STATE_KEY);
      return null;
    }

    return {
      taskId,
      to: String(parsed?.to || '').trim(),
      updatedAt,
      provider: String(parsed?.provider || 'system'),
    };
  } catch {
    targetStorage.removeItem(FOCUS_MODE_SYSTEM_CALL_STATE_KEY);
    return null;
  }
}

/**
 * Clear the current Focus Mode native-phone recovery snapshot.
 *
 * @param {object} [options={}] - Optional storage override.
 * @param {Storage} [options.storage] - Storage implementation, defaults to `window.sessionStorage`.
 * @returns {void}
 */
export function clearFocusModeSystemCallState({ storage } = {}) {
  const targetStorage =
    storage ||
    (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  targetStorage?.removeItem(FOCUS_MODE_SYSTEM_CALL_STATE_KEY);
}
